<template>
  <default-modal
    v-model="modalOpened"
    name="invoice-export-modal"
    width="md"
    :title="$t('invoice_export_modal_title')"
    :autofocus="$refs.customerSelect"
    :actionLabel="$t('export')"
    :successHandler="success ? null : handleExport"
  >
    <template slot="content" v-if="!success">
      <p v-html="$t('invoice_export_modal_body')"></p>

      <!-- Customers -->
      <q-field
        :label="$tc('customer', 2)"
        :labelWidth="4"
      >
        <multi-customer-select ref="customerSelect" v-model="formData.customer_id" />
      </q-field>

      <!-- Invoice Date Range -->
      <q-field
        :label="$t('invoice_date')"
        :labelWidth="4"
        :error="errors.has('startDate')"
        :error-label="errors.first('startDate')"
      >
        <div class="row no-wrap justify-between">
          <q-datetime
            v-model="formData.startDate"
            type="date"
            name="startDate"
            :format="$t('date_format')"
            clearable
          />

          <span class="on-right on-left">{{ $t('to') }}</span>

          <q-datetime
            v-model="formData.endDate"
            type="date"
            name="endDate"
            :format="$t('date_format')"
            clearable
          />
        </div>
      </q-field>

      <!-- Invoice Status -->
      <q-field :label="$t('invoice_status')" :labelWidth="4">
        <q-select v-model="formData.status" multiple :options="invoiceStatuses" />
      </q-field>
      <q-field :label="$t('export_format')" :labelWidth="4">
        <q-select v-model="formData.format" :options="formatOptions"></q-select>
      </q-field>
    </template>

    <template slot="content" v-else>
      <check-mark></check-mark>
      <h4 class="text-center">{{ $t('export_successful_title') }}</h4>
      <p class="text-center">{{ $t('export_successful_body') }}</p>
    </template>
  </default-modal>
</template>

<script>
  import CheckMark from 'components/Common/Animations/Checkmark.vue'
  import DefaultModal from 'components/Modal/DefaultModal.vue'
  import { InvoiceClient } from 'src/api/Invoices'
  import MultiCustomerSelect from 'src/components/Common/Inputs/MultiCustomerSelect.vue'
  import changeCase from 'change-case-object'
  import { createErrorFromResponse } from 'src/utils/error'
  import { date } from 'quasar'
  import { invoices } from 'src/config'

  export default {
    $_veeValidate: {
      validator: 'new'
    },

    data () {
      return {
        modalOpened: false,
        loading: false,
        success: false,
        formData: {
          customer_id: [],
          startDate: null,
          endDate: null,
          status: [
            'canceled',
            'open',
            'overdue',
            'paid',
            'partial',
            'storno'
          ],
          format: 'PDF'
        }
      }
    },

    computed: {
      invoiceStatuses () {
        return invoices.statuses.filter(status => !['draft', 'paid_cash_discount'].includes(status)).map(status => ({
          label: this.$t(status),
          value: status
        }))
      },

      formatOptions () {
        return ['PDF', 'DATEV'].map(format => {
          return { label: format, value: format }
        })
      },

      hasStartAndStopDate () {
        return !!this.formData.startDate && !!this.formData.endDate
      }
    },

    methods: {
      open () {
        this.modalOpened = true
        this.loading = false
        this.success = false
      },

      handleExport () {
        let data = {}
        if (this.formData.startDate && this.formData.endDate) {
          Object.assign(data, this.formData)
        }

        this.$validator.validateAll(data)
          .then(result => {
            if (!result) {
              return
            }

            if (this.hasStartAndStopDate && date.getDateDiff(this.formData.startDate, this.formData.endDate, 'seconds') > 0) {
              this.errors.add({
                field: 'startDate',
                msg: this.$t('start_before_end')
              })

              return
            }

            this.exportInvoices()
          })
      },

      exportInvoices () {
        this.loading = true

        new InvoiceClient()
          .export(changeCase.snake(this.__getFilters()))
          .then(() => {
            this.loading = false
            this.success = true
          })
          .catch(error => {
            this.loading = false

            createErrorFromResponse(error)
          })
      },

      __getFilters () {
        // Remove null values from filters
        let filters = {}
        Object.keys(this.formData).forEach(data => (this.formData[data] !== null && Object.assign(filters, { [data]: this.formData[data] })))

        // Map customers to their id
        if (filters.customer_id.length) {
          filters.customer_id = filters.customer_id.map(customer => customer.id)
        } else {
          delete filters.customer_id
        }

        if (filters.status.includes('paid')) {
          filters.status.push('paid_cash_discount')
        }

        return filters
      }
    },

    components: {
      CheckMark,
      MultiCustomerSelect,
      DefaultModal
    }
  }
</script>
